import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { BiHotel } from 'react-icons/bi';
import { TiWaves } from 'react-icons/ti';
import { IoMdAirplane } from 'react-icons/io';
import { AiTwotoneCar } from 'react-icons/ai';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import FlightItinerary from '../../TravelOption/FlightInformation/FlightItinerary';
import CarItinerary from '../../TravelOption/CarsInformation/CarItinerary';
import HotelItinerary from '../../TravelOption/HotelInformation/HotelItinerary';
import CruiseItinerary from '../../TravelOption/CruiseInformation/CruiseItinerary';
import TourItinerary from '../../TravelOption/TourInformation/TourItinerary';
import { TravelTypes } from '../../../constants';

const componentByType = {
  [TravelTypes.cruise]: CruiseItinerary,
  [TravelTypes.flight]: FlightItinerary,
  [TravelTypes.hotel]: HotelItinerary,
  [TravelTypes.carrental]: CarItinerary,
  [TravelTypes.tour]: TourItinerary,
};

const iconComponents = {
  [TravelTypes.cruise]: TiWaves,
  [TravelTypes.flight]: IoMdAirplane,
  [TravelTypes.hotel]: BiHotel,
  [TravelTypes.carrental]: AiTwotoneCar,
  [TravelTypes.tour]: TiWaves,
  [TravelTypes.other]: FaRegCalendarAlt,
};

const badgeLabels = {
  [TravelTypes.cruise]: 'Cruise',
  [TravelTypes.flight]: 'Flight',
  [TravelTypes.hotel]: 'Hotel',
  [TravelTypes.carrental]: 'Rental Car',
  [TravelTypes.tour]: 'Tour',
  [TravelTypes.other]: 'Itinerary',
};

const formatDate = (d) => dayjs(d).format('MMM D, YYYY');

const buildTravelersSummary = (input) => {
  const { adults = 0, seniors = 0, children = [], lapInfants = 0, seatInfants = 0 } = input || {};
  const childCount = Array.isArray(children) ? children.length : children || 0;
  return [
    [adults, 'adult', 'adults'],
    [seniors, 'senior', 'seniors'],
    [childCount, 'child', 'children'],
    [lapInfants, 'lap infant', 'lap infants'],
    [seatInfants, 'seat infant', 'seat infants'],
  ]
    .filter(([count]) => count > 0)
    .map(([count, sing, plur]) => `${count} ${count === 1 ? sing : plur}`)
    .join(', ');
};

const getFlightType = (itinerary) => {
  const segments = itinerary?.flightSegments ?? [];
  const isOneWay = itinerary?.isOneWay || false;
  const isRoundTrip = itinerary?.isTwoSegmentRoundTrip || false;
  const isMultiCity = itinerary?.isMultiCity || (!isRoundTrip && segments.length > 1);

  if (isOneWay) return 'One-way';
  if (isRoundTrip) return 'Round-trip';
  if (isMultiCity) return 'Multi-city';
  return 'Itinerary';
};

const buildHeaderFields = (segment, trip) => {
  const { itinerary, startDateTime, endDateTime } = segment;
  const input = trip?.roomSearchInput?.[0] || {};
  const passengerCount =
    input.numOfFlyingPeople > 0
      ? input.numOfFlyingPeople
      : input.adults +
        (input.seniors || 0) +
        (input.children || []).length +
        (input.seatInfants || 0);

  if (segment?.mode === TravelTypes.hotel) {
    return [
      {
        label: 'Dates',
        value: `${formatDate(startDateTime)} – ${formatDate(endDateTime)}`,
      },
      { label: 'Guests', value: passengerCount || 1 },
    ];
  }
  if (segment?.mode === TravelTypes.flight) {
    const flightSegments = itinerary?.flightSegments || [];

    const firstSegment = flightSegments[0] || {};
    const lastSegment = flightSegments[flightSegments.length - 1] || {};

    const origin = firstSegment.originAirportCode || '';
    const destination = lastSegment.destinationAirportCode || '';

    const flightType = getFlightType(itinerary);

    const start = firstSegment.departureDateTime || startDateTime;
    const end = lastSegment.arrivalDateTime || endDateTime;

    let tripText = '';
    if (flightType === 'Round-trip') {
      tripText = `${flightType} · ${origin} ⇄ ${destination}`;
    } else if (flightType === 'Multi-city') {
      tripText = `${flightType} · Multiple destinations`;
    } else {
      tripText = `${flightType} · ${origin} → ${destination}`;
    }

    const dateValue =
      start && end && flightType !== 'One-way'
        ? `${formatDate(start)} – ${formatDate(end)}`
        : formatDate(start || startDateTime);

    return [
      { label: flightType === 'One-way' ? 'Date' : 'Dates', value: dateValue },
      { label: 'Travelers', value: buildTravelersSummary(input) || '—' },
      { label: 'Trip', value: tripText },
    ];
  }
  if (segment?.mode === TravelTypes.carrental) {
    return [
      { label: 'Pick-up', value: formatDate(startDateTime) },
      { label: 'Drop-off', value: formatDate(endDateTime) },
      { label: 'Passengers', value: passengerCount || 1 },
    ];
  }
  if (segment?.mode === TravelTypes.cruise) {
    const nights = itinerary?.itineraryDetail?.itineraryNumNights || 0;
    return [
      {
        label: 'Dates',
        value:
          startDateTime && endDateTime
            ? `${formatDate(startDateTime)} – ${formatDate(endDateTime)}`
            : formatDate(startDateTime || endDateTime),
      },
      {
        label: 'Duration',
        value: `${nights} nights`,
      },
      {
        label: 'Ship',
        value: itinerary?.itineraryDetail?.ship?.shipName || 'Cruise',
      },
      {
        label: 'Travelers',
        value: buildTravelersSummary(input) || `${passengerCount} traveler`,
      },
    ];
  }
  if (segment?.mode === TravelTypes.tour) {
    return [];
  }
  if (startDateTime || endDateTime) {
    return [
      {
        label: 'Dates',
        value:
          startDateTime && endDateTime
            ? `${formatDate(startDateTime)} – ${formatDate(endDateTime)}`
            : formatDate(startDateTime || endDateTime),
      },
    ];
  }
  return [];
};

const SegmentCard = ({ segment, trip, doShowPrice }) => {
  const { travelAgency } = useSelector((store) => store.user);
  const Component = componentByType[segment?.mode];

  const fields = buildHeaderFields(segment, trip) || [];
  const IconType = iconComponents[segment?.mode] || FaRegCalendarAlt;
  const badgeLabel = badgeLabels[segment?.mode] || 'Itinerary';

  const hotelProps =
    segment?.mode === TravelTypes.hotel
      ? {
          doShowChatGpt: !!travelAgency?.settings?.doShowChatGPTOnBrochure,
          compactImage: true,
        }
      : {};

  const extraProps = { ...hotelProps };

  const hotelWrapperOverride =
    segment?.mode === TravelTypes.hotel ? { wrapperClassName: 'px-3 py-3' } : {};

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4">
      {fields.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-y-3">
          <div className="flex flex-wrap gap-x-10 gap-y-3">
            {fields.map((field) => (
              <div key={field.label}>
                <div className="text-xs uppercase text-gray-500 font-medium tracking-wide">
                  {field.label}
                </div>
                <div className="text-sm text-gray-900 mt-1">{field.value}</div>
              </div>
            ))}
          </div>
          <div className="sm:p-1 bg-theme bg-opacity-25 rounded-lg text-theme flex flex-row items-center">
            <IconType className="ml-2" />
            <span className="px-2">{badgeLabel}</span>
          </div>
        </div>
      )}
      {Component ? (
        <Component
          tripId={trip?.id}
          itinerary={segment.itinerary}
          doShowPrice={doShowPrice}
          tripRoomSearchInput={trip?.roomSearchInput || trip?.staterooms || undefined}
          doHaveBorderAround={false}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...hotelWrapperOverride}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...extraProps}
        />
      ) : (
        <div className="px-5 py-4 text-sm text-gray-500">
          Itinerary details unavailable for this item.
        </div>
      )}
    </div>
  );
};

SegmentCard.propTypes = {
  segment: PropTypes.shape({
    mode: PropTypes.number,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    itinerary: PropTypes.shape({}),
  }).isRequired,
  trip: PropTypes.shape({
    id: PropTypes.string,
    roomSearchInput: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  doShowPrice: PropTypes.bool,
};

SegmentCard.defaultProps = {
  trip: undefined,
  doShowPrice: true,
};

export default SegmentCard;
